import React, { useEffect, useState } from 'react'

import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { Box, Tab } from '@mui/material'
import useAuth from '../../../../hooks/useAuth'
import { CompanyIssues } from './CompanyIssues'
import { CompanyWarrants } from './CompanyWarrants'
import IssueApi from '../../../../api/IssueApi'
import WarrantApi from '../../../../api/WarrantApi'

const sortAndSetIssuesFunction = (issues) => {
    if (!issues) return []

    const sortedIssues = issues.sort((a, b) => {
        // First sort by statusComplete (0 before 1)
        if (a.statusComplete !== b.statusComplete) {
            return a.statusComplete - b.statusComplete
        }

        // If statusComplete values are the same, sort by date
        const s1 = new Date(a.subEndDate ? a.subEndDate : a.publicationDate)
        const s2 = new Date(b.subEndDate ? b.subEndDate : b.publicationDate)

        return s2 - s1 // Newest dates first
    })

    return sortedIssues
}

export const CompanyEvents = ({ selectedIsin, setSelectedIsin, onArchiveIsin }) => {
    const [issues, setIssues] = useState(null)
    const [warrants, setWarrants] = useState(null)
    const [selectedTab, setSelectedTab] = useState('issues')
    const { isAuthenticated } = useAuth()
    const issueApi = new IssueApi()
    const warrantApi = new WarrantApi()

    useEffect(() => {
        if (!selectedIsin) return

        if (selectedIsin.instrumentType === 'warrant') {
            setSelectedTab('warrants')
        } else {
            setSelectedTab('issues')
        }
    }, [selectedIsin])

    useEffect(() => {
        if (!isAuthenticated || !selectedIsin) return

        const fetchData = async () => {
            const result = await issueApi.findByCompanyId(selectedIsin.companyId, false)

            const filteredIssues = result.filter(
                (issue) =>
                    issue.type === 'Rights issue' &&
                    (selectedIsin.instrumentType === 'unit' ? issue.isinUnits === null : issue.isinRights === null),
            )
            setIssues(sortAndSetIssuesFunction(filteredIssues))
        }
        fetchData()
    }, [isAuthenticated, selectedIsin, setIssues])

    useEffect(() => {
        if (!isAuthenticated || !selectedIsin) return

        const fetchData = async () => {
            const result = await warrantApi.findByCompanyId(selectedIsin.companyId)
            // keep only one of each warrant (by id)
            const uniqueWarrants = result.filter(
                (w, i) => !w.unlisted && w.isinWarrant === null && result.findIndex((w2) => w2.id === w.id) === i,
            )

            uniqueWarrants.sort((a, b) => {
                // Get the newest subEndDate for warrant `a`
                const s1 =
                    a.subscriptionPeriods && a.subscriptionPeriods.length
                        ? new Date(Math.max(...a.subscriptionPeriods.map((period) => new Date(period.subEndDate))))
                        : null

                // Get the newest subEndDate for warrant `b`
                const s2 =
                    b.subscriptionPeriods && b.subscriptionPeriods.length
                        ? new Date(Math.max(...b.subscriptionPeriods.map((period) => new Date(period.subEndDate))))
                        : null

                // Sort by newest subEndDate first
                if (s1 === null && s2 === null) return 0 // Both are null, they are equal
                if (s1 === null) return 1 // `a` has no date, `b` should come first
                if (s2 === null) return -1 // `b` has no date, `a` should come first

                return s2 - s1 // Newest dates first
            })

            setWarrants(uniqueWarrants)
        }
        fetchData()
    }, [isAuthenticated, selectedIsin, setWarrants])

    if (selectedIsin === null) return null

    return (
        <TabContext value={selectedTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
                    <Tab label='Emissioner' value='issues' disabled={selectedIsin.instrumentType === 'warrant'} />
                    <Tab
                        label='Teckningsoptioner'
                        value='warrants'
                        disabled={selectedIsin.instrumentType !== 'warrant'}
                    />
                </TabList>
            </Box>
            <TabPanel value='issues'>
                <CompanyIssues
                    issues={issues}
                    selectedIsin={selectedIsin}
                    setSelectedIsin={setSelectedIsin}
                    onArchiveIsin={onArchiveIsin}
                />
            </TabPanel>
            <TabPanel value='warrants'>
                <CompanyWarrants
                    warrants={warrants}
                    selectedIsin={selectedIsin}
                    setSelectedIsin={setSelectedIsin}
                    onArchiveIsin={onArchiveIsin}
                />
            </TabPanel>
        </TabContext>
    )
}
